import { useCallback } from "react";
import { SubmitHandler } from "react-hook-form";
import { OrderTypeResponse, SideResponse } from "../../../codegen-api";
import { COLORS } from "../../../constants/design/colors";
import { getDisplayInstrumentName } from "../../../utils/displayName";
import { alignPriceToStep, toStepFixed } from "../../../utils/format";
import { resolveSpotOrderSize } from "./spotOrderSize";
import { getTxExplorerUrl } from "../../../utils/chain";
import {
  ToastEnum,
  ToastStatusEnum,
} from "../../../utils/toast";
import { IPerpsFormFieldValues } from "./form";
import { usePerpsTradeFormSubmit } from "./usePerpsTradeFormSubmit";

export function usePerpsTradeFormSubmitSpot(
  ctx: ReturnType<typeof usePerpsTradeFormSubmit>
) {
  const {
    perpInstrument,
    orderDirection,
    orderType,
    placeSpotOrder,
    tradeUseUSDCTerms,
    getRealContractSize,
    contractPriceStep,
    addToast,
    addErrorToast,
    commonFormTranslations,
    showOrderCreatedAnimation,
    form,
    resetAmountInput,
    onClose,
    playSound,
    setIsLoading,
    orderbookData,
    realContractSize,
  } = ctx;

  const submitSpotOrder = useCallback<SubmitHandler<IPerpsFormFieldValues>>(
    async (values) => {
      if (!perpInstrument) return;
      const mkt = perpInstrument as any;
      try {
        setIsLoading(true);
        // If user entered amount in USDC terms, convert to base token quantity.
        // Every path rounds DOWN to the market's step: a buy must never spend
        // more quote, and a sell never sell more base, than the trader entered.
        // Aligned at submit too: Enter and the submit shortcut skip the blur.
        const limitPriceText =
          orderType === OrderTypeResponse.Limit
            ? alignPriceToStep(
                values.price,
                contractPriceStep.price_step,
                contractPriceStep.price_precision,
                orderDirection === SideResponse.Sell ? "up" : "down"
              )
            : undefined;
        let rawSize: number;
        if (tradeUseUSDCTerms) {
          rawSize =
            orderType === OrderTypeResponse.Limit && Number(limitPriceText) > 0
              ? // For limit orders, use the limit price for conversion
                Number(values.amount) / Number(limitPriceText)
              : // For market orders, use orderbook to calculate quantity
                getRealContractSize(values.amount, true);
        } else {
          rawSize = Number(values.amount);
        }

        const sized = resolveSpotOrderSize({
          rawSize,
          amountStep: contractPriceStep.amount_step,
          amountPrecision: contractPriceStep.amount_precision,
          minOrderAmount: contractPriceStep.min_order_amount,
        });
        if (sized.belowMinimum) {
          addErrorToast(
            <p>Order Size Error</p>,
            `Order size must be at least ${sized.minimum} ${
              perpInstrument.underlying_asset ?? ""
            }`.trim()
          );
          return;
        }
        const baseAmount = toStepFixed(
          sized.size,
          contractPriceStep.amount_step,
          contractPriceStep.amount_precision
        );
        const response = await placeSpotOrder({
          instrument_id: perpInstrument.instrument_id,
          base_currency: mkt.base_currency,
          quote_currency: mkt.quote_currency,
          side: orderDirection,
          order_type: orderType,
          amount: baseAmount,
          price: limitPriceText,
        });

        if (response?.order_id) {
          playSound("order_placed", true);
          addToast(
            {
              type: ToastEnum.INFO,
              header: (
                <p>
                  {getDisplayInstrumentName(perpInstrument.instrument_name) || perpInstrument.instrument_name} {orderDirection}
                </p>
              ),
              subheader: (
                <span>
                  <span style={{ color: orderDirection === SideResponse.Buy ? COLORS.up.one : COLORS.down.one }}>
                    {orderType} {orderDirection}
                  </span>
                  {response.tx_hash && (
                    <>
                      {" · "}
                      <a href={getTxExplorerUrl(response.tx_hash)} target="_blank" rel="noopener noreferrer" style={{ color: COLORS.link.one, textDecoration: "underline" }}>
                        View tx
                      </a>
                    </>
                  )}
                </span>
              ),
              stats: [
                {
                  label:
                    orderDirection === SideResponse.Buy
                      ? commonFormTranslations("buy_amount")
                      : commonFormTranslations("sell_amount"),
                  value: values.amount,
                },
              ],
              status: ToastStatusEnum.SUCCESS,
            },
            4000
          );
          showOrderCreatedAnimation();
          form.reset();
          resetAmountInput();
          onClose?.();
        }
      } catch (error: any) {
        addErrorToast(
          <p>
            {orderDirection === SideResponse.Buy ? "Buy" : "Sell"} Failed
          </p>,
          error?.message || "Failed to place spot order"
        );
      } finally {
        setIsLoading(false);
      }
    },
    [
      perpInstrument,
      orderDirection,
      orderType,
      placeSpotOrder,
      tradeUseUSDCTerms,
      getRealContractSize,
      contractPriceStep.amount_precision,
      contractPriceStep.amount_step,
      contractPriceStep.min_order_amount,
      contractPriceStep.price_step,
      contractPriceStep.price_precision,
      addToast,
      addErrorToast,
      commonFormTranslations,
      showOrderCreatedAnimation,
      form,
      resetAmountInput,
      onClose,
      playSound,
    ]
  );

  // if buy, check asks liquidity
  let insufficientLiquidity = ctx.usdExceedsBookDepth;
  // Only a size can be short of liquidity, and only against a book we have:
  // an empty size on a one-sided book used to show "Insufficient market
  // liquidity" before anything was typed, and a book not loaded yet read as empty.
  const canCheckLiquidity = realContractSize > 0 && Boolean(orderbookData);

  if (canCheckLiquidity && orderDirection === SideResponse.Buy) {
    const totalAsksSize =
      orderbookData?.asks?.reduce((prev, ask) => {
        const askSize = Number(ask[1] || 0);
        return prev + askSize;
      }, 0) || 0;
    if (!totalAsksSize || totalAsksSize < realContractSize) {
      insufficientLiquidity = true;
    }
  }

  // if sell, check bids liquidity
  if (canCheckLiquidity && orderDirection === SideResponse.Sell) {
    const totalBidsSize =
      orderbookData?.bids?.reduce((prev, ask) => {
        const bidSize = Number(ask[1] || 0);
        return prev + bidSize;
      }, 0) || 0;
    if (!totalBidsSize || totalBidsSize < realContractSize) {
      insufficientLiquidity = true;
    }
  }

  return {
    ...ctx,
    submitSpotOrder,
    insufficientLiquidity,
  };
}
